import type { VerificationAction } from "./provenance.model.ts";

/**
 * Which surface the provenance library is being rendered on.
 *
 * `src/provenance/` is a component library with no screen of its own, mounted
 * by two screens that want different things from it. That difference used to be
 * implicit — it fell out of the draft/recorded union, an `editable` boolean, a
 * nullable prop and an optional handler — so nothing anywhere declared what a
 * surface was FOR, and answering "does this button appear on the work record"
 * meant reading four files.
 *
 * This is the one file that names a surface. No component imports it: the model
 * takes a `SurfaceConfig` and bakes the answer into the view-model, so no
 * component ever branches on which screen it is on.
 */
export type SurfaceName = "compose" | "record" | "dialog";

/**
 * How an entry offers its verification.
 *
 * A union rather than a boolean beside a list, so "states its standing AND
 * offers a list of controls with reasons" is not a shape anybody can write.
 *
 * `act` — every offered control renders, and a closed one carries the sentence
 * saying why. This is the composing surface: you are being shown what you can
 * do to a thing you are making.
 *
 * `state` — what the record holds is stated first, and only controls the server
 * says this reader may actually use render beneath it. A closed control's
 * reason is not lost: it is already a sentence in the statement.
 */
export type VerificationOffer =
  | { mode: "act"; acts: readonly VerificationAction["key"][] }
  | { mode: "state"; acts: readonly VerificationAction["key"][] };

export interface SurfaceConfig {
  /**
   * What a DRAFT offers. Always `act`.
   *
   * Nothing has been said about a draft — it is not on the registry, so there
   * are no counts and no positions — which means there is no standing for a
   * `state` panel to state. Both surfaces carry drafts: the work record grows
   * one every time you press Add Entry.
   */
  draft: VerificationOffer;
  recorded: VerificationOffer;
  documents: "edit" | "read";
  /**
   * Whether a paper may be accepted or rejected here — OND-62.
   *
   * SEPARATE FROM `documents`, and the two point in opposite directions, which
   * is why it could not be folded into it. `documents: "edit"` is the composing
   * right — upload, re-file, take back — and belongs to whoever is assembling
   * the dossier. Judging one is a READER'S act: the estate looks at what the
   * dealer filed and says which papers stand. So the surface that edits
   * documents offers no verdicts, and the surface that only reads them offers
   * them.
   *
   * It takes away, like every other field here: `"offer"` still defers to the
   * server's `canAttest` on the entry, and `"hidden"` is the wizard, whose
   * documents are files on the device with no row to hang a verdict off.
   */
  verdicts: "offer" | "hidden";
  withdraw: boolean;
  /** Who has been asked and not answered. Asker's side only, either way. */
  asks: "outstanding" | "hidden";
  timeline: boolean;
}

/**
 * The two surfaces, hard-coded.
 *
 * READ THE RULE BEFORE ADDING A THIRD. A surface can only ever TAKE AWAY. The
 * server's `canAttest` / `canCurate` / `myStance` and the draft-vs-recorded
 * union remain the authority on what is possible; a config narrows what is
 * offered. Two things make that structural rather than a promise:
 *
 *   - `verificationFor` builds the controls and never sees a surface;
 *     `offeredHere` sees the surface and never sees an entry. Neither has both
 *     in scope, so there is nowhere to write `config.acts.includes(k) || …`.
 *   - Everywhere else a config value may appear only to the LEFT of a `?` or as
 *     a filter predicate — never in a disjunction with a server field.
 */
export const PROVENANCE_SURFACES: Record<SurfaceName, SurfaceConfig> = {
  /**
   * The register wizard, step 3. Everything here is unsent: nothing has been
   * filed, so nothing can be withdrawn and nobody can have been asked yet.
   */
  compose: {
    draft: { mode: "act", acts: ["verify", "dispute", "comment", "request"] },
    // Unreachable — the wizard's items are all drafts — but the type asks, and
    // an answer that matched the record's would be a lie about a screen that
    // cannot show one.
    recorded: { mode: "act", acts: ["verify", "dispute", "comment", "request"] },
    documents: "edit",
    verdicts: "hidden",
    withdraw: false,
    asks: "hidden",
    timeline: true,
  },

  /**
   * The Add-to-artwork-history modal, and the third surface — added under the
   * rule above, which it obeys: it only takes away.
   *
   * `dispute` and `comment` are gone, and neither is a loss. There is nothing to
   * dispute about a claim that does not exist yet, and a comment is context you
   * add BESIDE a claim — a modal whose whole job is to compose one has the claim
   * itself for that. What is left is the two acts the design draws: putting your
   * own name on it, and asking somebody else to.
   *
   * `verify` stays because it is half of what makes this feature work. Almost
   * every chain of title is signed first by whoever wrote it, and a modal that
   * could ask three strangers to corroborate an entry its own author had not
   * stood behind would be a strange thing to have built.
   */
  dialog: {
    draft: { mode: "act", acts: ["verify", "request"] },
    // Unreachable — a modal composes and closes, and nothing recorded is ever
    // rendered in it — but the type asks. Answering with the record's shape
    // would be a lie about a screen that cannot show one.
    recorded: { mode: "act", acts: ["verify", "request"] },
    documents: "edit",
    verdicts: "hidden",
    withdraw: false,
    asks: "hidden",
    timeline: false,
  },

  /**
   * The work record's Provenance tab. Reading a record is reading.
   *
   * `comment` is off the recorded branch deliberately: a comment is context you
   * add while composing a claim, and the point of this surface is that looking
   * at a work does not ask you to do anything to it. Verify, dispute and ask
   * stay offered — the server decides which of them this particular reader
   * sees, which is how an expert the collection was shared with still has
   * somewhere to sign.
   */
  record: {
    draft: { mode: "act", acts: ["verify", "dispute", "comment", "request"] },
    recorded: { mode: "state", acts: ["verify", "dispute", "request"] },
    documents: "read",
    verdicts: "offer",
    withdraw: true,
    asks: "outstanding",
    timeline: false,
  },
};
